import { fold } from './data';
import { h, icon } from './dom';
import type { Politician } from './types';

interface SearchOptions {
  options: () => Politician[];
  onPick: (p: Politician) => void;
  /** Renders the small picture next to each suggestion. */
  avatar: (p: Politician) => HTMLElement;
  subtitle: (p: Politician) => string;
}

const MAX_RESULTS = 8;

function matches(options: Politician[], query: string): Politician[] {
  const q = fold(query.trim());
  if (!q) return [];
  const scored: [number, Politician][] = [];
  for (const p of options) {
    const name = fold(p.name);
    if (name.startsWith(q)) scored.push([0, p]);
    else if (name.split(/[\s-]+/).some((w) => w.startsWith(q))) scored.push([1, p]);
    else if (name.includes(q)) scored.push([2, p]);
  }
  scored.sort((a, b) => a[0] - b[0] || a[1].name.localeCompare(b[1].name, 'sk'));
  return scored.slice(0, MAX_RESULTS).map(([, p]) => p);
}

export function createSearch({ options, onPick, avatar, subtitle }: SearchOptions): HTMLElement {
  let results: Politician[] = [];
  let active = 0;

  const list = h('ul', { class: 'suggestions', role: 'listbox', id: 'suggestions', hidden: true });
  const input = h('input', {
    type: 'text',
    class: 'search-input',
    placeholder: 'Napíš meno politika…',
    autocomplete: 'off',
    autocapitalize: 'words',
    spellcheck: 'false',
    enterkeyhint: 'go',
    role: 'combobox',
    'aria-expanded': 'false',
    'aria-controls': 'suggestions',
    'aria-autocomplete': 'list',
    'aria-label': 'Meno politika',
  });

  const pick = (p: Politician | undefined) => {
    if (!p) return;
    input.value = '';
    close();
    onPick(p);
  };

  const close = () => {
    results = [];
    list.hidden = true;
    input.setAttribute('aria-expanded', 'false');
  };

  const draw = () => {
    list.replaceChildren(
      ...results.map((p, i) =>
        h(
          'li',
          {
            role: 'option',
            class: i === active ? 'active' : '',
            'aria-selected': String(i === active),
            // Keep focus in the input so the keyboard stays open on phones.
            onPointerdown: (e: Event) => e.preventDefault(),
            onClick: () => pick(p),
          },
          avatar(p),
          h('span', { class: 'sugg-text' }, h('span', { class: 'sugg-name' }, p.name), h('span', { class: 'sugg-sub' }, subtitle(p))),
        ),
      ),
    );
    if (input.value.trim() && !results.length) {
      list.append(h('li', { class: 'empty' }, 'Nikto taký tu nie je.'));
    }
    list.hidden = !input.value.trim();
    input.setAttribute('aria-expanded', String(!list.hidden));
  };

  input.addEventListener('input', () => {
    results = matches(options(), input.value);
    active = 0;
    draw();
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!results.length) return;
      e.preventDefault();
      active = (active + (e.key === 'ArrowDown' ? 1 : -1) + results.length) % results.length;
      draw();
      list.children[active]?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(results[active]);
    } else if (e.key === 'Escape') {
      close();
    }
  });
  input.addEventListener('blur', () => setTimeout(close, 150));
  input.addEventListener('focus', () => {
    if (input.value.trim()) input.dispatchEvent(new Event('input'));
  });

  const submit = h(
    'button',
    { class: 'search-submit', type: 'button', 'aria-label': 'Hádať', onClick: () => pick(results[active]) },
    icon('enter'),
  );

  return h('div', { class: 'search' }, h('div', { class: 'search-box' }, input, submit), list);
}
